import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    setIsSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setIsSubmitting(false);
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }
    formData.delete('website_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9n16lobdkpk99j7gfc0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const serverMsg = (parsed as Record<string, unknown> | null)?.meta?.message
          || (parsed as Record<string, unknown> | null)?.message
          || (parsed as Record<string, unknown> | null)?.meta?.detail
          || responseText
          || '提交失败，请稍后重试';
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        {/* Hero */}
        <section className="w-full px-4 md:px-6 lg:px-10 py-8 md:py-12">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-foreground-100 font-heading mb-4">
              关于大师兄影视
            </h1>
            <p className="text-sm md:text-base text-foreground-400 max-w-2xl mx-auto leading-relaxed">
              大师兄影视是一个致力于为广大影迷提供高品质免费观影体验的在线平台。
              我们汇聚海量影视资源，以极速加载、高清画质、纯净无广告为核心特色，
              让每一位用户都能畅享流畅舒适的观影时光。
            </p>
          </div>
        </section>

        {/* Brand Intro */}
        <section className="w-full px-4 md:px-6 lg:px-10 pb-10 md:pb-14">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
              <div className="bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/40">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-500/10 text-primary-400 mb-4">
                  <i className="ri-lightbulb-flash-line text-xl"></i>
                </div>
                <h2 className="text-lg md:text-xl font-bold text-foreground-100 font-heading mb-3">
                  品牌理念
                </h2>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  我们相信，好的影视内容应该被更多人看到。大师兄影视秉承"开放、免费、优质"的理念，
                  打破传统影视平台的付费壁垒，让每一位用户都能无门槛地享受影视文化的魅力。
                  无论是经典老片还是最新热播，我们都用心为你呈现。
                </p>
              </div>
              <div className="bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/40">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-500/10 text-accent-400 mb-4">
                  <i className="ri-star-smile-line text-xl"></i>
                </div>
                <h2 className="text-lg md:text-xl font-bold text-foreground-100 font-heading mb-3">
                  用户体验
                </h2>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  从页面加载速度到视频播放流畅度，从界面交互设计到内容推荐算法，
                  我们始终以用户体验为中心。无需注册、无需付费、无广告打扰，
                  打开大师兄影视，即刻开启沉浸式观影之旅。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full px-4 md:px-6 lg:px-10 pb-10 md:pb-14">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              {[
                { value: '10万+', label: '影视资源', icon: 'ri-film-line' },
                { value: '500万+', label: '日活用户', icon: 'ri-user-heart-line' },
                { value: '99.9%', label: '播放成功率', icon: 'ri-check-double-line' },
                { value: '4.9', label: '用户评分', icon: 'ri-star-line' },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="bg-background-100 rounded-2xl p-5 md:p-6 border border-background-200/40 text-center"
                >
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-500/10 text-primary-400">
                    <i className={`${stat.icon} text-lg`}></i>
                  </div>
                  <p className="text-2xl md:text-3xl font-bold text-foreground-100 font-heading">
                    {stat.value}
                  </p>
                  <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full px-4 md:px-6 lg:px-10 pb-10 md:pb-14">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-100 font-heading text-center mb-8">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {[
                {
                  name: '影迷小王',
                  avatar: 'https://readdy.ai/api/search-image?query=Young%20Chinese%20man%20portrait%2C%20friendly%20smile%2C%20warm%20lighting%2C%20professional%20headshot%2C%20clean%20background%2C%20natural%20expression&width=100&height=100&seq=avatar-01&orientation=squarish',
                  text: '用了大师兄影视半年了，片源真的很全！最新上映的片子都能找到，而且播放特别流畅，不用等缓冲，体验太好了。',
                },
                {
                  name: '追剧达人',
                  avatar: 'https://readdy.ai/api/search-image?query=Young%20Chinese%20woman%20portrait%2C%20warm%20smile%2C%20soft%20lighting%2C%20professional%20headshot%2C%20clean%20background%2C%20natural%20casual%20style&width=100&height=100&seq=avatar-02&orientation=squarish',
                  text: '之前用别的平台总是被广告烦得要死，大师兄影视完全没有广告！画质也好，看剧看得很舒服，已经推荐给身边所有朋友了。',
                },
                {
                  name: '电影发烧友',
                  avatar: 'https://readdy.ai/api/search-image?query=Middle-aged%20Chinese%20man%20portrait%2C%20thoughtful%20expression%2C%20warm%20lighting%2C%20professional%20headshot%2C%20clean%20background%2C%20intellectual%20style&width=100&height=100&seq=avatar-03&orientation=squarish',
                  text: '作为一个影迷，大师兄影视的分类和推荐算法真的很懂我。每次推荐的都是我喜欢的类型，省去了找片的烦恼，赞！',
                },
              ].map((testimonial) => (
                <div
                  key={testimonial.name}
                  className="bg-background-100 rounded-2xl p-5 md:p-6 border border-background-200/40"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground-200">{testimonial.name}</p>
                      <div className="flex items-center gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    &ldquo;{testimonial.text}&rdquo;
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full px-4 md:px-6 lg:px-10 pb-14 md:pb-20">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-100 font-heading text-center mb-8">
              联系我们
            </h2>
            <div className="bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/40">
              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleContactSubmit}
                noValidate
              >
                {/* Honeypot */}
                <div className="website-alt-field">
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm text-foreground-400 mb-1.5">
                      姓名
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full bg-background-200/60 border border-background-300/40 rounded-xl px-4 py-2.5 text-sm text-foreground-200 placeholder-foreground-600 focus:outline-none focus:border-primary-500/50 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm text-foreground-400 mb-1.5">
                      邮箱
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full bg-background-200/60 border border-background-300/40 rounded-xl px-4 py-2.5 text-sm text-foreground-200 placeholder-foreground-600 focus:outline-none focus:border-primary-500/50 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="contact-subject" className="block text-sm text-foreground-400 mb-1.5">
                    主题
                  </label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    required
                    className="w-full bg-background-200/60 border border-background-300/40 rounded-xl px-4 py-2.5 text-sm text-foreground-200 placeholder-foreground-600 focus:outline-none focus:border-primary-500/50 transition-all"
                    placeholder="请输入主题"
                  />
                </div>
                <div className="mb-6">
                  <label htmlFor="contact-message" className="block text-sm text-foreground-400 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={4}
                    required
                    maxLength={500}
                    className="w-full bg-background-200/60 border border-background-300/40 rounded-xl px-4 py-2.5 text-sm text-foreground-200 placeholder-foreground-600 focus:outline-none focus:border-primary-500/50 transition-all resize-none"
                    placeholder="请输入您的留言（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-600 mt-1">最多500字</p>
                </div>

                {formError && (
                  <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                    {formError}
                  </div>
                )}
                {formStatus === 'success' && (
                  <div className="mb-4 p-3 rounded-xl bg-green-500/10 border border-green-500/20 text-green-400 text-sm">
                    提交成功！我们会尽快与您联系。
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-secondary-500 disabled:cursor-not-allowed text-white rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中...
                    </span>
                  ) : (
                    '提交留言'
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: 'ri-mail-line', label: '邮箱', value: 'contact@dashixiong.com' },
                { icon: 'ri-customer-service-2-line', label: '客服', value: '7x24小时在线' },
                { icon: 'ri-map-pin-line', label: '地址', value: '北京市朝阳区' },
              ].map((info) => (
                <div key={info.label} className="flex items-center gap-3 bg-background-100 rounded-xl p-4 border border-background-200/40">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-primary-500/10 text-primary-400">
                    <i className={`${info.icon} text-lg`}></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-foreground-500">{info.label}</p>
                    <p className="text-sm text-foreground-200 font-medium truncate">{info.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SEO Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'AboutPage',
              name: '关于大师兄影视',
              description: '大师兄影视是一个致力于为广大影迷提供高品质免费观影体验的在线平台。汇聚海量影视资源，极速加载、高清画质、纯净无广告。',
              url: 'https://www.dashixiong.com/about',
              isPartOf: {
                '@type': 'WebSite',
                name: '大师兄影视',
                url: 'https://www.dashixiong.com/',
              },
            }),
          }}
        />
      </main>

      <Footer />
    </>
  );
}